import React from "react";
import type {
  components,
  PatientContractId,
  PatientDocumentScheduleId,
  PatientId,
} from "../../../shared/schema/schema";
import type { ContentItem } from "../layout/documentLayout";

// What the entity-backed questions (medications, ICD / medical diagnosis codes, primary physician)
// need from the review beyond their answers: whose records they show, and how an editor reports
// its changes and its open forms back to the review (edited marker + PDF regeneration on close,
// close guard). Provided once by DocumentReviewContent around the document.

/** An entity editor's work that closing the review would lose or cut short. */
export interface EntityEditorActivity {
  /** An add / edit form or a confirm dialog is open */
  hasOpenForm: boolean;
  /** A change of the patient's record is in flight */
  isSaving: boolean;
}

export const IDLE_ENTITY_ACTIVITY: EntityEditorActivity = { hasOpenForm: false, isSaving: false };

export interface ReviewEntityContextValue {
  /** The document's content (the PDF picks e.g. the first medicationProfile / icdCodes). */
  content: ContentItem[];
  /** The document type (CMS485 / FC_CMS485 print their medications as text). */
  documentType: components["schemas"]["PatientDocumentTypes"];
  patientContractId: PatientContractId;
  patientId: PatientId;
  scheduledDocId: PatientDocumentScheduleId | null;
  /** The open entity editor's activity (reported while it is open). */
  onActivityChange: (activity: EntityEditorActivity) => void;
  /** A change of the patient's record went through from the editor of `questionId`. */
  onEntityChanged: (questionId: number) => void;
}

const ReviewEntityContext = React.createContext<ReviewEntityContextValue | null>(null);

export const ReviewEntityProvider = ReviewEntityContext.Provider;

export function useReviewEntity(): ReviewEntityContextValue {
  const value = React.useContext(ReviewEntityContext);
  if (value === null) {
    throw new Error("useReviewEntity must be used inside ReviewEntityProvider");
  }
  return value;
}
